import React, { useState } from 'react';
import Icon from './ui-stubs';
import { startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, isSameMonth } from 'date-fns';
import { appMonthNameYear, appWeekday } from '../../../../utils/appDateFormat';
import { parseApiDateOnly } from '@/utils/apiDate';

const CalendarView = ({ tasks, onTaskClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'

  const getCalendarDays = () => {
    if (viewMode === 'month') {
      const start = startOfWeek(startOfMonth(currentDate));
      const end = endOfWeek(endOfMonth(currentDate));
      return eachDayOfInterval({ start, end });
    } else {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return eachDayOfInterval({ start, end });
    }
  };

  const getTasksForDate = (date) => {
    return tasks?.filter(task => isSameDay((parseApiDateOnly(task?.dueDate) ?? new Date(NaN)), date));
  };

  const handlePrevious = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(new Date(currentDate?.getTime() - 7 * 24 * 60 * 60 * 1000));
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(new Date(currentDate?.getTime() + 7 * 24 * 60 * 60 * 1000));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-500';
      case 'High':
        return 'bg-orange-500';
      case 'Medium':
        return 'bg-yellow-500';
      case 'Low':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const calendarDays = getCalendarDays();
  const weekDays = eachDayOfInterval({
    start: startOfWeek(new Date()),
    end: endOfWeek(new Date()),
  }).map((d) => appWeekday(d, 'short'));

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-bold text-gray-900">
            {appMonthNameYear(currentDate)}
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevious}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Icon name="ChevronLeft" size={20} className="text-gray-600" />
            </button>
            <button
              onClick={handleToday}
              className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              Today
            </button>
            <button
              onClick={handleNext}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Icon name="ChevronRight" size={20} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center space-x-2 bg-gray-100 rounded-md p-1">
          <button
            onClick={() => setViewMode('month')}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'month' ?'bg-white text-gray-900 shadow-sm' :'text-gray-600 hover:text-gray-900'
            }`}
          >
            Month
          </button>
          <button
            onClick={() => setViewMode('week')}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'week' ?'bg-white text-gray-900 shadow-sm' :'text-gray-600 hover:text-gray-900'
            }`}
          >
            Week
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="border border-border rounded-lg overflow-hidden">
        {/* Week Day Headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-border">
          {weekDays?.map((day) => (
            <div
              key={day}
              className="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays?.map((day, index) => {
            const dayTasks = getTasksForDate(day);
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = isSameMonth(day, currentDate);

            return (
              <div
                key={index}
                className={`min-h-[120px] border-r border-b border-border p-2 ${
                  !isCurrentMonth ? 'bg-gray-50' : 'bg-white'
                } hover:bg-gray-50 transition-colors`}
              >
                {/* Day Number */}
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-sm font-medium ${
                      isToday
                        ? 'bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center'
                        : isCurrentMonth
                        ? 'text-gray-900' :'text-gray-400'
                    }`}
                  >
                    {day.getDate()}
                  </span>
                  {dayTasks?.length > 0 && (
                    <span className="text-xs text-gray-500 font-medium">
                      {dayTasks?.length}
                    </span>
                  )}
                </div>

                {/* Tasks for this day */}
                <div className="space-y-1">
                  {dayTasks?.slice(0, 3)?.map((task) => (
                    <button
                      key={task?.id}
                      onClick={() => onTaskClick(task)}
                      className={`w-full text-left px-2 py-1 rounded text-xs hover:shadow-sm transition-shadow border-l-2 ${
                        task?.priority === 'Critical' ? 'bg-red-50 border-l-red-500 text-red-900' :
                        task?.priority === 'High' ? 'bg-orange-50 border-l-orange-500 text-orange-900' :
                        task?.priority === 'Medium'? 'bg-yellow-50 border-l-yellow-500 text-yellow-900' : 'bg-green-50 border-l-green-500 text-green-900'
                      }`}
                    >
                      <div className="font-medium truncate">{task?.title}</div>
                      <div className="flex items-center space-x-1 mt-0.5">
                        {task?.assignees?.slice(0, 2)?.map((assignee) => (
                          <div
                            key={assignee?.id}
                            className="w-4 h-4 bg-primary text-white rounded-full flex items-center justify-center text-[8px] font-semibold"
                            title={assignee?.name}
                          >
                            {assignee?.avatar?.charAt(0)}
                          </div>
                        ))}
                      </div>
                    </button>
                  ))}
                  {dayTasks?.length > 3 && (
                    <div className="text-xs text-gray-500 text-center py-1">
                      +{dayTasks?.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-end space-x-4 text-xs">
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-red-500 rounded"></div>
          <span className="text-gray-600">Critical</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-orange-500 rounded"></div>
          <span className="text-gray-600">High</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-yellow-500 rounded"></div>
          <span className="text-gray-600">Medium</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-green-500 rounded"></div>
          <span className="text-gray-600">Low</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
